/**
 * Idempotent script injection shared by the GA4 and GTM loaders.
 *
 * A script that fails to load — blocked by an ad-blocker, a content security
 * policy or an offline network — resolves to `false` rather than rejecting, so
 * callers can mark the destination unavailable and carry on silently.
 */

const loaded = new Map<string, Promise<boolean>>();

/**
 * Injects a `<script src>` once per URL.
 *
 * @returns a promise resolving to `true` when the script loaded, `false` otherwise.
 */
export function loadScript(src: string): Promise<boolean> {
  const existing = loaded.get(src);
  if (existing) return existing;

  const pending = new Promise<boolean>((resolve) => {
    try {
      const script = document.createElement("script");
      script.async = true;
      script.src = src;
      script.addEventListener("load", () => resolve(true));
      script.addEventListener("error", () => resolve(false));
      document.head.appendChild(script);
    } catch (error) {
      console.warn("[Analytics] Failed to inject script:", error);
      resolve(false);
    }
  });

  loaded.set(src, pending);
  return pending;
}

/** Clears the injection cache. Test-only. */
export function resetLoadedScripts(): void {
  loaded.clear();
}
